import { useRef, useState } from 'react';
import PageMeta from '../../components/SEO/PageMeta';
import SiteFooter from '../../components/Footer/SiteFooter';
import SiteNavigation from '../../components/Navigation/SiteNavigation';
import {
  buildTesterApplicationPayload,
  createFormSession,
  submitTesterApplication
} from './testerApplicationForm';
import './PreAlpha.css';

const expectations = [
  'Complete the assigned tasks inside the Atlas tester tools.',
  'Fill out the required test reports after each assigned task.',
  'Describe problems clearly and honestly, including what happened before the problem appeared.',
  'Test unfinished software patiently and understand that some features may change or fail.',
  'Protect any private pre-alpha information, builds, screenshots, and instructions shared with you.'
];

export default function PreAlpha() {
  const formSession = useRef(createFormSession());
  const [status, setStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    const formElement = event.currentTarget;
    const formData = new FormData(formElement);
    const payload = buildTesterApplicationPayload(formData, formSession.current);

    setIsSubmitting(true);
    setStatus({
      type: 'pending',
      message: 'Submitting your tester application securely…'
    });

    try {
      const response = await submitTesterApplication(payload);

      formElement.reset();
      formSession.current = createFormSession();
      setStatus({
        type: 'success',
        message: response.message || 'Your tester application was submitted successfully.'
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'We could not submit your application right now. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <PageMeta
        title="Atlas Pre-Alpha Testing | Apply to Test Atlas"
        description="Apply to test Atlas with real files and workflows across multiple folders and drives. Help improve the central Atlas desktop experience before public release."
        path="/pre-alpha"
      />
      <main className="tester-page">
        <section className="tester-hero">
          <SiteNavigation />
          <div className="page-shell tester-hero__content">
            <p className="section-eyebrow">Atlas pre-alpha testing</p>
            <h1>Help test Atlas with real files and real workflows.</h1>
            <p className="tester-hero__lead">
              Atlas is looking for testers who manage photos, documents, projects, and other files across multiple folders or drives. Your testing will help us make that scattered information easier to find and organize from one centralized workspace.
            </p>
            <p className="tester-hero__lead">
              Atlas works with the locations people already use and is designed to leave original files in place rather than forcing everything into a new folder structure.
            </p>
          </div>
        </section>

        <section className="content-section">
          <div className="page-shell tester-intro">
            <div>
              <p className="section-eyebrow">What testing involves</p>
              <h2>Pre-alpha testing is structured work, not early access for casual browsing.</h2>
            </div>
            <div className="tester-expectations">
              {expectations.map((expectation) => (
                <div className="tester-expectation" key={expectation}>
                  <span aria-hidden="true">✓</span>
                  <p>{expectation}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="content-section tester-application-section">
          <div className="page-shell">
            <div className="tester-form-heading">
              <p className="section-eyebrow">Tester application</p>
              <h2>Tell us why you would make a dependable Atlas tester.</h2>
              <p>
                We are looking for people who will follow test instructions, perform assigned tasks, complete reports, and explain their experience carefully enough that the development team can act on it.
              </p>
            </div>

            <form className="tester-form" onSubmit={handleSubmit}>
              <div className="tester-form__honeypot" aria-hidden="true">
                <label>
                  Leave this field blank
                  <input name="website" type="text" tabIndex="-1" autoComplete="off" />
                </label>
              </div>

              <div className="tester-form__grid">
                <label>
                  Full name
                  <input name="name" type="text" autoComplete="name" required />
                </label>

                <label>
                  Email address
                  <input name="email" type="email" autoComplete="email" required />
                </label>

                <label>
                  Location and time zone
                  <input name="location" type="text" placeholder="Example: Maine, Eastern Time" required />
                </label>

                <label>
                  Occupation or primary computer use
                  <input name="occupation" type="text" placeholder="Example: photographer, student, office work" required />
                </label>

                <label className="tester-form__wide">
                  Operating system and basic computer details
                  <input name="system" type="text" placeholder="Example: Windows 11, desktop PC, 16 GB RAM" required />
                </label>

                <label className="tester-form__wide">
                  Describe your typical digital workflow
                  <textarea
                    name="workflow"
                    rows="5"
                    minLength="20"
                    placeholder="Minimum 20 characters. Tell us what kinds of files, folders, projects, drives, and applications you work with."
                    required
                  />
                </label>

                <label className="tester-form__wide">
                  Why do you think you would make a good Atlas tester?
                  <textarea
                    name="why"
                    rows="6"
                    minLength="20"
                    placeholder="Minimum 20 characters. Explain why you are dependable, observant, patient, and able to follow testing tasks and complete useful reports."
                    required
                  />
                </label>

                <label className="tester-form__wide">
                  What problems do you hope Atlas could improve in your own work?
                  <textarea
                    name="needs"
                    rows="5"
                    minLength="20"
                    placeholder="Minimum 20 characters. Describe the real organization or workflow problems you currently experience."
                    required
                  />
                </label>

                <label className="tester-form__wide">
                  How much time could you normally give to an assigned testing round?
                  <select name="availability" required defaultValue="">
                    <option value="" disabled>Select an answer</option>
                    <option>Less than 1 hour</option>
                    <option>1–2 hours</option>
                    <option>2–4 hours</option>
                    <option>More than 4 hours when needed</option>
                  </select>
                </label>
              </div>

              <fieldset className="tester-agreement">
                <legend>Required tester agreement</legend>
                <label>
                  <input name="agreementTasks" type="checkbox" required />
                  <span>I understand that I will be expected to perform assigned tasks in the Atlas tester tools and complete the requested reports.</span>
                </label>
                <label>
                  <input name="agreementPreAlpha" type="checkbox" required />
                  <span>I understand that pre-alpha software may contain errors, incomplete features, and unexpected behavior.</span>
                </label>
                <label>
                  <input name="agreementConfidentiality" type="checkbox" required />
                  <span>I agree not to share private builds, test instructions, screenshots, or confidential information without permission.</span>
                </label>
              </fieldset>

              <div className="tester-form__submit">
                <button className="button button--primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting application…' : 'Submit tester application'}
                </button>
                <p>Applications are submitted directly to the Atlas testing team.</p>
              </div>

              {status && (
                <p
                  className={`tester-form__status tester-form__status--${status.type}`}
                  role="status"
                  aria-live="polite"
                >
                  {status.message}
                </p>
              )}
            </form>
          </div>
        </section>

        <SiteFooter />
      </main>
    </>
  );
}
